import React, { useEffect, useState, useMemo } from 'react';
import useEscapeKey from '../hooks/useEscapeKey';
import Modal from '../components/Modal';
import { getEmployeeDisplayName } from './AdminPanel/utils/helpers';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Prefer policy_type on the request. Managers are not admins, so the policy
// list fetch often fails and a missing lookup used to render sick time as PTO.
function labelForRequest(req, policies) {
  const explicit = String(req?.policy_type || '').toLowerCase();
  if (explicit === 'sick') return 'Sick';
  if (explicit === 'pto') return 'PTO';
  const policyId = req?.policy_id;
  if (!policyId) return 'PTO';
  const policy = (policies || []).find(p => (p.id || p.name) === policyId);
  return policy?.policy_type === 'sick' ? 'Sick' : 'PTO';
}

export default function ManagerPanel({ user }) {
  const [activeTab, setActiveTab] = useState('requests');
  const [requestsTab, setRequestsTab] = useState('action-required'); // 'action-required', 'upcoming' or 'past'

  // requests state
  const [requests, setRequests] = useState([]);
  const [upcomingRequests, setUpcomingRequests] = useState([]);
  const [pastRequests, setPastRequests] = useState([]);
  const [reqLoading, setReqLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedReq, setSelectedReq] = useState(null);
  const [reqModalOpen, setReqModalOpen] = useState(false);

  // reports state
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);

  // policies state (needed for request types)
  const [policies, setPolicies] = useState([]);

  // History modal state
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedEmployeeHistory, setSelectedEmployeeHistory] = useState(null);
  
  // Enhanced reports UI state
  const [searchTerm, setSearchTerm] = useState('');

  // Enhanced request review state
  const [employeeReports, setEmployeeReports] = useState({});

  // Sorting state for Requests table
  const [requestSortField, setRequestSortField] = useState('start_date');
  const [requestSortOrder, setRequestSortOrder] = useState('desc'); // 'asc' | 'desc'

  // Tooltip state for advance booking badge
  const [hoveredBadge, setHoveredBadge] = useState(null);
  const [hoveredCalendarBadge, setHoveredCalendarBadge] = useState(null);
  const [hoveredAnniversaryBadge, setHoveredAnniversaryBadge] = useState(null);

  // Escape key handler for modal
  useEscapeKey(() => {
    if (reqModalOpen) setReqModalOpen(false);
    if (historyModalOpen) setHistoryModalOpen(false);
  });

  // Watch for hash changes to switch to action-required tab
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#','');
      if (hash === 'manager-action-required') {
        setActiveTab('requests');
        setRequestsTab('action-required');
      }
    };

    // Check initial hash
    handleHashChange();
    
    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Handle header click to toggle sorting for requests
  const handleRequestSort = (field) => {
    if (field === requestSortField) {
      setRequestSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setRequestSortField(field);
      setRequestSortOrder('asc');
    }
  };

  // Fetch PTO policies for request type determination
  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/pto_policies`, {
          headers: {
            'Content-Type': 'application/json',
            'X-User-Email': user?.email || ''
          },
          credentials: 'include'
        });
        if (!res.ok) throw new Error(`Failed to fetch policies: ${res.status}`);
        const data = await res.json();
        setPolicies(data.policies || []);
      } catch (e) {
        console.error(e);
      }
    };
    fetchPolicies();
  }, [user]);

  // Derived list of PTO requests based on status filter
  const filteredRequests = useMemo(() => {
    if (statusFilter === 'all') return requests;
    const nf = statusFilter.toLowerCase();
    return requests.filter((r) => {
      const s = (r.status || '').toLowerCase();
      if (nf === 'rejected') return s === 'rejected' || s === 'denied';
      return s === nf;
    });
  }, [requests, statusFilter]);

  // Memoized, dynamically sorted list of requests
  const sortedFilteredRequests = useMemo(() => {
    const requestsToSort = [...filteredRequests];
    
    const getRequestType = (req) => labelForRequest(req, policies);
    
    requestsToSort.sort((a, b) => {
      let valA, valB;
      
      switch (requestSortField) {
        case 'employee':
          valA = (a.userEmail || a.user_email || '').toLowerCase();
          valB = (b.userEmail || b.user_email || '').toLowerCase();
          break;
        case 'type':
          valA = getRequestType(a).toLowerCase();
          valB = getRequestType(b).toLowerCase();
          break;
        case 'start_date':
          valA = a.startDate || a.start_date || '';
          valB = b.startDate || b.start_date || '';
          break;
        case 'end_date':
          valA = a.endDate || a.end_date || '';
          valB = b.endDate || b.end_date || '';
          break;
        case 'days':
          valA = parseInt(a.days || 0);
          valB = parseInt(b.days || 0);
          break;
        case 'status':
          valA = (a.status || '').toLowerCase();
          valB = (b.status || '').toLowerCase();
          break;
        default:
          valA = '';
          valB = '';
      }
      
      if (requestSortField === 'days') {
        // Numeric comparison for days
        if (valA < valB) return requestSortOrder === 'asc' ? -1 : 1;
        if (valA > valB) return requestSortOrder === 'asc' ? 1 : -1;
        return 0;
      } else {
        // String comparison for other fields
        if (valA < valB) return requestSortOrder === 'asc' ? -1 : 1;
        if (valA > valB) return requestSortOrder === 'asc' ? 1 : -1;
        return 0;
      }
    });
    return requestsToSort;
  }, [filteredRequests, requestSortField, requestSortOrder, policies]);

  // fetch requests
  useEffect(() => {
    if (activeTab !== 'requests') return;
    setReqLoading(true);
    fetch(`${API_BASE}/api/manager/pto_requests`, {
      headers: {
        'Content-Type': 'application/json',
        'X-User-Email': user.email,
      },
      credentials: 'include',
    })
      .then((r) => r.json())
      .then((d) => {
        // Keep the old requests array for backwards compatibility
        const sorted = (d.requests || []).sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
        setRequests(sorted);
        
        // Use the new upcoming/past structure if available
        if (d.upcoming && d.past) {
          setUpcomingRequests(d.upcoming);
          setPastRequests(d.past);
        } else {
          // Fallback: separate requests manually if API doesn't provide the split
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const upcoming = [];
          const past = [];
          
          sorted.forEach(req => {
            try {
              const startDate = new Date(req.start_date);
              startDate.setHours(0, 0, 0, 0);
              
              if (startDate >= today) {
                upcoming.push(req);
              } else {
                past.push(req);
              }
            } catch {
              past.push(req);
            }
          });
          
          setUpcomingRequests(upcoming);
          setPastRequests(past);
        }
      })
      .catch(console.error)
      .finally(() => setReqLoading(false));
  }, [activeTab, user]);

  // fetch reports and populate employee data for both tabs
  useEffect(() => {
    if (activeTab !== 'reports') return;
    setReportsLoading(true);
    fetch(`${API_BASE}/api/manager/pto_reports`, {
      headers: { 'Content-Type': 'application/json', 'X-User-Email': user.email },
      credentials: 'include',
    })
      .then((r) => r.json())
      .then((d) => {
        const reportsData = d.reports || [];
        setReports(reportsData);
        
        // Also populate employee reports for the requests tab
        const employeeReportsData = {};
        reportsData.forEach(report => {
          employeeReportsData[report.email] = report;
        });
        setEmployeeReports(employeeReportsData);
      })
      .catch(console.error)
      .finally(() => setReportsLoading(false));
  }, [activeTab, user]);

  // Fetch employee data when requests tab is active (if not already loaded)
  useEffect(() => {
    if (activeTab !== 'requests' || Object.keys(employeeReports).length > 0) return;
    
    // Fetch employee data for the requests tab
    fetch(`${API_BASE}/api/manager/pto_reports`, {
      headers: { 'Content-Type': 'application/json', 'X-User-Email': user.email },
      credentials: 'include',
    })
      .then((r) => r.json())
      .then((d) => {
        const reportsData = d.reports || [];
        const employeeReportsData = {};
        reportsData.forEach(report => {
          employeeReportsData[report.email] = report;
        });
        setEmployeeReports(employeeReportsData);
      })
      .catch(console.error);
  }, [activeTab, user, employeeReports]);

  // No longer need individual fetchEmployeeData function - data is fetched in bulk above

  // approve/reject/delete
  const handleReqAction = async (id, action) => {
    try {
      const res = await fetch(`${API_BASE}/api/manager/pto_requests/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Email': user.email },
        credentials: 'include',
        body: JSON.stringify({ action }),
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      
      // Update all relevant state arrays
      const updatedRequest = data.request;
      
      setRequests((prev) => prev.map((r) => (r.id === id ? updatedRequest : r)));
      setUpcomingRequests((prev) => prev.map((r) => (r.id === id ? updatedRequest : r)));
      setPastRequests((prev) => prev.map((r) => (r.id === id ? updatedRequest : r)));
    } catch (e) {
      alert(e.message);
    }
  };

  // Function to open history modal
  const openHistoryModal = (employee) => {
    setSelectedEmployeeHistory(employee);
    setHistoryModalOpen(true);
  };

  // Function to format dates properly (avoiding timezone issues)
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      // Handle both date strings and datetime strings
      if (dateStr.includes('T')) {
        // This is a datetime string, format it normally
        return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      } else {
        // This is a date string, parse as local date to avoid timezone issues
        const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
        const date = new Date(year, month - 1, day); // month is 0-indexed
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      }
    } catch {
      return dateStr;
    }
  };

  // Enhanced Reports UI Helper Functions
  const getUtilizationColor = (pct) => {
    if (pct == null) return 'var(--symbolica-muted)';
    if (pct < 5) return '#ef4444'; // Very low usage - red
    if (pct < 15) return '#f59e0b'; // Low usage - yellow
    if (pct > 30) return '#dc2626'; // High usage - red
    return '#22c55e'; // Normal usage - green
  };

  const getStatusBadge = (status) => {
    const baseStyle = {
      fontSize: '11px',
      fontWeight: '600',
      padding: '4px 8px',
      borderRadius: '12px',
      textTransform: 'capitalize'
    };
    
    switch (status?.toLowerCase()) {
      case 'approved':
        return { ...baseStyle, background: '#dcfce7', color: '#15803d' };
      case 'pending':
        return { ...baseStyle, background: '#fef3c7', color: '#d97706' };
      case 'rejected':
      case 'denied':
        return { ...baseStyle, background: '#fecaca', color: '#dc2626' };
      default:
        return { ...baseStyle, background: '#F0EDE6', color: 'var(--symbolica-muted)' };
    }
  };

  const ProgressBar = ({ value, max, color = 'var(--symbolica-black)', height = 6 }) => (
    <div style={{
      width: '100%',
      height: `${height}px`,
      background: 'var(--symbolica-warm-border)',
      borderRadius: `${height/2}px`,
      overflow: 'hidden'
    }}>
      <div style={{
        width: `${Math.min(100, Math.max(0, (value / max) * 100))}%`,
        height: '100%',
        background: color,
        borderRadius: `${height/2}px`,
        transition: 'width 0.3s ease'
      }} />
    </div>
  );

  const MetricCard = ({ icon, label, value, subtitle, color = 'var(--symbolica-black)', progress }) => (
    <div style={{
      background: '#F6F4F0',
      border: '1px solid var(--symbolica-warm-border)',
      borderRadius: '10px',
      padding: '12px',
      textAlign: 'center'
    }}>
      <div style={{ fontSize: '16px', marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: '18px', fontWeight: '700', color, marginBottom: 2 }}>
        {value}
      </div>
      <div style={{ fontSize: '11px', color: 'var(--symbolica-muted)', fontWeight: '500' }}>
        {label}
      </div>
      {subtitle && (
        <div style={{ fontSize: '10px', color: 'var(--symbolica-muted)', marginTop: 2 }}>
          {subtitle}
        </div>
      )}
      {progress && (
        <div style={{ marginTop: 8 }}>
          <ProgressBar value={progress.value} max={progress.max} color={color} />
        </div>
      )}
    </div>
  );

  // Fuzzy search function
  const fuzzyMatch = (searchTerm, text) => {
    if (!searchTerm || !text) return true;
    
    const search = searchTerm.toLowerCase();
    const target = text.toLowerCase();
    
    // Direct substring match (most relevant)
    if (target.includes(search)) return true;
    
    // Fuzzy character sequence match
    let searchIndex = 0;
    for (let i = 0; i < target.length && searchIndex < search.length; i++) {
      if (target[i] === search[searchIndex]) {
        searchIndex++;
      }
    }
    return searchIndex === search.length;
  };

  // Filter reports based on search term
  const filteredReports = useMemo(() => {
    if (!searchTerm.trim()) return reports;
    
    return reports.filter(rep => {
      const fullName = getEmployeeDisplayName({ 
        first_name: rep.first_name, 
        last_name: rep.last_name, 
        email: rep.email 
      });
      
      const searchableFields = [
        rep.email || '',
        rep.first_name || '',
        rep.last_name || '',
        fullName,
        rep.title || ''
      ];
      
      return searchableFields.some(field => fuzzyMatch(searchTerm, field));
    });
  }, [reports, searchTerm]);

  return (
    <div className="page-wrap">
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <button className={`secondary-button ${activeTab === 'requests' ? 'active' : ''}`} onClick={() => setActiveTab('requests')}>
          Time Off Requests
        </button>
        <button className={`secondary-button ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}>
          Time Off Reports
        </button>
      </div>

      {activeTab === 'requests' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
            <h2 className="content-title" style={{ margin: 0 }}>My Team – Time Off</h2>
            {requestsTab !== 'action-required' && (
              <label style={{ fontSize: 14 }}>
                Status:
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ marginLeft: 8, padding: '6px 8px' }}>
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected/Denied</option>
                </select>
              </label>
            )}
          </div>
          
          {/* Tab Navigation for Action Required/Upcoming/Past */}
          <div style={{ display: 'flex', flexWrap: 'wrap', borderBottom: '1px solid var(--symbolica-warm-border)', marginBottom: 16 }}>
            <button
              onClick={() => setRequestsTab('action-required')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: requestsTab === 'action-required' ? '#dc2626' : 'var(--symbolica-muted)',
                borderBottom: requestsTab === 'action-required' ? '2px solid #dc2626' : '2px solid transparent',
                position: 'relative'
              }}
            >
              Action Required ({[...upcomingRequests, ...pastRequests].filter(r => r.status === 'pending').length})
              {[...upcomingRequests, ...pastRequests].filter(r => r.status === 'pending').length > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#dc2626',
                  borderRadius: '50%',
                  fontSize: '0',
                  pointerEvents: 'none'
                }}>
                  •
                </span>
              )}
            </button>
            <button
              onClick={() => setRequestsTab('upcoming')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: requestsTab === 'upcoming' ? 'var(--symbolica-black)' : 'var(--symbolica-muted)',
                borderBottom: requestsTab === 'upcoming' ? '2px solid var(--symbolica-black)' : '2px solid transparent'
              }}
            >
              Upcoming ({upcomingRequests.filter(r => statusFilter === 'all' || r.status === statusFilter || (statusFilter === 'rejected' && (r.status === 'rejected' || r.status === 'denied'))).length})
            </button>
            <button
              onClick={() => setRequestsTab('past')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: requestsTab === 'past' ? 'var(--symbolica-black)' : 'var(--symbolica-muted)',
                borderBottom: requestsTab === 'past' ? '2px solid var(--symbolica-black)' : '2px solid transparent'
              }}
            >
              Past ({pastRequests.filter(r => statusFilter === 'all' || r.status === statusFilter || (statusFilter === 'rejected' && (r.status === 'rejected' || r.status === 'denied'))).length})
            </button>
          </div>

          {reqLoading ? (
            <div>Loading requests…</div>
          ) : (
            <div>
              {(() => {
                // Choose the appropriate requests based on the selected tab
                let currentRequests;
                if (requestsTab === 'action-required') {
                  // Action Required: only pending requests from both upcoming and past
                  currentRequests = [...upcomingRequests, ...pastRequests].filter(r => r.status === 'pending');
                } else if (requestsTab === 'upcoming') {
                  // Upcoming: all upcoming requests regardless of status
                  currentRequests = upcomingRequests;
                } else {
                  // Past: all past requests regardless of status  
                  currentRequests = pastRequests;
                }
                
                // Apply status filter (but for Action Required tab, we only show pending anyway)
                const displayRequests = requestsTab === 'action-required' ? currentRequests : currentRequests.filter(r => {
                  if (statusFilter === 'all') return true;
                  const status = r.status?.toLowerCase() || '';
                  if (statusFilter === 'rejected') return status === 'rejected' || status === 'denied';
                  return status === statusFilter.toLowerCase();
                });
                
                // Apply sorting
                const sortedRequests = [...displayRequests].sort((a, b) => {
                  let valA, valB;
                  
                  switch (requestSortField) {
                    case 'employee':
                      valA = (a.user_email || '').toLowerCase();
                      valB = (b.user_email || '').toLowerCase();
                      break;
                    case 'type':
                      const getRequestType = (req) => labelForRequest(req, policies).toLowerCase();
                      valA = getRequestType(a);
                      valB = getRequestType(b);
                      break;
                    case 'start_date':
                      valA = a.start_date || '';
                      valB = b.start_date || '';
                      break;
                    case 'end_date':
                      valA = a.end_date || '';
                      valB = b.end_date || '';
                      break;
                    case 'days':
                      valA = parseInt(a.days || 0);
                      valB = parseInt(b.days || 0);
                      break;
                    case 'status':
                      valA = (a.status || '').toLowerCase();
                      valB = (b.status || '').toLowerCase();
                      break;
                    default:
                      valA = '';
                      valB = '';
                  }
                  
                  if (requestSortField === 'days') {
                    if (valA < valB) return requestSortOrder === 'asc' ? -1 : 1;
                    if (valA > valB) return requestSortOrder === 'asc' ? 1 : -1;
                    return 0;
                  } else {
                    if (valA < valB) return requestSortOrder === 'asc' ? -1 : 1;
                    if (valA > valB) return requestSortOrder === 'asc' ? 1 : -1;
                    return 0;
                  }
                });
                
                if (sortedRequests.length === 0) {
                  const tabDisplayName = requestsTab === 'action-required' ? 'requests requiring action' : 
                                        requestsTab === 'upcoming' ? 'upcoming requests' : 'past requests';
                  return (
                    <div style={{ textAlign: 'center', padding: '32px', color: 'var(--symbolica-muted)' }}>
                      No {tabDisplayName} found{statusFilter !== 'all' && requestsTab !== 'action-required' ? ` with status "${statusFilter}"` : ''}.
                    </div>
                  );
                }
                
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {sortedRequests.map((req) => {
                      const getRequestType = (req) => labelForRequest(req, policies);
                      
                      const requestType = getRequestType(req);
                      const employeeData = employeeReports[req.user_email];
                      
                      // Calculate impact of approval
                      const isPTORequest = requestType === 'PTO';
                      const currentBalance = isPTORequest ? employeeData?.balance : employeeData?.sick_balance;
                      const impactBalance = currentBalance ? {
                        ...currentBalance,
                        pending: Math.max(0, (currentBalance.pending || 0) - parseFloat(req.days || 0)),
                        used: (currentBalance.used || 0) + parseFloat(req.days || 0),
                        remaining: currentBalance.remaining ? Math.max(0, currentBalance.remaining - parseFloat(req.days || 0)) : 0
                      } : null;
                      
                      const employeeName = employeeData ? 
                        `${employeeData.first_name} ${employeeData.last_name}`.trim() || employeeData.email 
                        : req.user_email;
                      
                      return (
                        <div key={req.id} style={{
                          border: '1px solid var(--symbolica-warm-border)',
                          borderRadius: '12px',
                          padding: '24px',
                          backgroundColor: 'var(--symbolica-card)',
                          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                          transition: 'all 0.2s ease',
                          position: 'relative'
                        }}>
                          {/* Header */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                            <div>
                              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: 'var(--symbolica-black)' }}>
                                {employeeName}
                              </h3>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                                <span style={{
                                  padding: '4px 12px',
                                  borderRadius: '16px',
                                  fontSize: '13px',
                                  fontWeight: '500',
                                  background: requestType === 'Sick' ? '#fef3c7' : '#dbeafe',
                                  color: requestType === 'Sick' ? '#92400e' : '#1e40af'
                                }}>
                                  {requestType}
                                </span>
                                <span style={{
                                  padding: '4px 12px',
                                  borderRadius: '16px',
                                  fontSize: '13px',
                                  fontWeight: '500',
                                  background: req.status === 'approved' ? '#dcfce7' : req.status === 'rejected' || req.status === 'denied' ? '#fef2f2' : '#fef3c7',
                                  color: req.status === 'approved' ? '#166534' : req.status === 'rejected' || req.status === 'denied' ? '#dc2626' : '#92400e'
                                }}>
                                  {req.status}
                                </span>
                                {employeeData && (
                                  <span style={{ fontSize: '14px', color: 'var(--symbolica-muted)' }}>
                                    {employeeData.title || '—'}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Action Buttons */}
                            {req.status === 'pending' && (
                              <div style={{ display: 'flex', gap: '12px' }}>
                                <button 
                                  className="primary-button" 
                                  style={{ fontSize: '14px', padding: '10px 20px' }}
                                  onClick={() => handleReqAction(req.id, 'approve')}
                                >
                                  Approve
                                </button>
                                <button 
                                  className="secondary-button" 
                                  style={{ fontSize: '14px', padding: '10px 20px' }}
                                  onClick={() => handleReqAction(req.id, 'reject')}
                                >
                                  Reject
                                </button>
                              </div>
                            )}
                          </div>
                          
                                                     {/* Request Details */}
                           <div style={{ display: 'grid', gridTemplateColumns: requestsTab === 'action-required' ? 'repeat(auto-fit, minmax(300px, 1fr))' : '1fr', gap: '24px' }}>
                             {/* Left Column - Request Info */}
                             <div>
                               <h4 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: '600', color: 'var(--symbolica-black)' }}>
                                 Request Details
                               </h4>
                               <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                 <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                   <span style={{ color: 'var(--symbolica-muted)' }}>Dates:</span>
                                   <span style={{ fontWeight: '500' }}>
                                     {formatDate(req.start_date)} → {formatDate(req.end_date)}
                                   </span>
                                 </div>
                                 <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                   <span style={{ color: 'var(--symbolica-muted)' }}>Duration:</span>
                                   <span style={{ fontWeight: '500' }}>{req.days} day{req.days !== 1 ? 's' : ''}</span>
                                 </div>
                                 <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                   <span style={{ color: 'var(--symbolica-muted)' }}>Reason:</span>
                                   <span style={{ fontWeight: '500', textAlign: 'right', maxWidth: '200px' }}>
                                     {req.reason || 'No reason provided'}
                                   </span>
                                 </div>
                               </div>
                             </div>
                             
                             {/* Right Column - Employee Data - Only show on Action Required tab */}
                             {requestsTab === 'action-required' && (
                               <div>
                                 {employeeData ? (
                                <>
                                  <h4 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: '600', color: 'var(--symbolica-black)' }}>
                                    Current Balance
                                  </h4>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                                    {isPTORequest ? (
                                      <>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                          <span style={{ color: 'var(--symbolica-muted)' }}>Remaining:</span>
                                          <span style={{ fontWeight: '500' }}>{currentBalance?.remaining || 0}</span>
                                        </div>
                                        {currentBalance?.allow_full_year_upfront && currentBalance?.accrued_to_date != null && currentBalance?.accrued_to_date !== currentBalance?.total && (
                                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: 'var(--symbolica-muted)' }}>Accrued to Date:</span>
                                            <span style={{ fontWeight: '500' }}>{currentBalance.accrued_to_date}</span>
                                          </div>
                                        )}
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                          <span style={{ color: 'var(--symbolica-muted)' }}>Used:</span>
                                          <span style={{ fontWeight: '500' }}>{currentBalance?.used || 0}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                          <span style={{ color: 'var(--symbolica-muted)' }}>Pending:</span>
                                          <span style={{ fontWeight: '500' }}>{currentBalance?.pending || 0}</span>
                                        </div>
                                      </>
                                    ) : (
                                      // Sick time balance
                                      <>
                                        {currentBalance?.is_usage_only ? (
                                          <>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                              <span style={{ color: 'var(--symbolica-muted)' }}>Used:</span>
                                              <span style={{ fontWeight: '500' }}>{currentBalance?.used || 0}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                              <span style={{ color: 'var(--symbolica-muted)' }}>Pending:</span>
                                              <span style={{ fontWeight: '500' }}>{currentBalance?.pending || 0}</span>
                                            </div>
                                            <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)' }}>
                                              (Usage-only policy)
                                            </div>
                                          </>
                                        ) : (
                                          <>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                              <span style={{ color: 'var(--symbolica-muted)' }}>Remaining:</span>
                                              <span style={{ fontWeight: '500' }}>{currentBalance?.remaining || 0}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                              <span style={{ color: 'var(--symbolica-muted)' }}>Used:</span>
                                              <span style={{ fontWeight: '500' }}>{currentBalance?.used || 0}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                              <span style={{ color: 'var(--symbolica-muted)' }}>Pending:</span>
                                              <span style={{ fontWeight: '500' }}>{currentBalance?.pending || 0}</span>
                                            </div>
                                          </>
                                        )}
                                      </>
                                    )}
                                  </div>
                                  
                                  {/* Impact of Approval */}
                                  {req.status === 'pending' && impactBalance && (
                                    <>
                                      <h4 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: '600', color: 'var(--symbolica-black)' }}>
                                        If Approved
                                      </h4>
                                      <div style={{ 
                                        padding: '12px', 
                                        backgroundColor: '#f0f9ff', 
                                        borderRadius: '8px',
                                        border: '1px solid #e0f2fe'
                                      }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#0369a1', fontSize: '13px' }}>New Used:</span>
                                            <span style={{ fontWeight: '500', color: '#0369a1', fontSize: '13px' }}>
                                              {impactBalance.used}
                                            </span>
                                          </div>
                                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#0369a1', fontSize: '13px' }}>New Pending:</span>
                                            <span style={{ fontWeight: '500', color: '#0369a1', fontSize: '13px' }}>
                                              {impactBalance.pending}
                                            </span>
                                          </div>
                                          {!currentBalance?.is_usage_only && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                              <span style={{ color: '#0369a1', fontSize: '13px' }}>New Available:</span>
                                              <span style={{ fontWeight: '500', color: '#0369a1', fontSize: '13px' }}>
                                                {impactBalance.remaining}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </>
                                                               ) : (
                                   <div style={{ textAlign: 'center', padding: '20px', color: 'var(--symbolica-muted)' }}>
                                     Employee data not available
                                   </div>
                                 )}
                               </div>
                             )}
                           </div>
                          
                          {/* Recent History - Only show on Action Required tab */}
                          {requestsTab === 'action-required' && employeeData && (employeeData.history || []).length > 0 && (
                            <div style={{ marginTop: '24px', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                              <h4 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: '600', color: 'var(--symbolica-black)' }}>
                                Recent Time Off History
                              </h4>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {(employeeData.history || []).slice(0, 3).map((historyItem, index) => (
                                  <div key={historyItem.id || index} style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    padding: '8px 0',
                                    borderBottom: index < 2 ? '1px solid #f9fafb' : 'none'
                                  }}>
                                    <div>
                                      <span style={{ fontSize: '14px', fontWeight: '500' }}>
                                        {formatDate(historyItem.start_date)} → {formatDate(historyItem.end_date)}
                                      </span>
                                      <span style={{ fontSize: '13px', color: 'var(--symbolica-muted)', marginLeft: '8px' }}>
                                        ({historyItem.days} day{historyItem.days !== 1 ? 's' : ''})
                                      </span>
                                    </div>
                                    <span style={{
                                      padding: '2px 8px',
                                      borderRadius: '12px',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      background: historyItem.status === 'approved' ? '#dcfce7' : '#fef2f2',
                                      color: historyItem.status === 'approved' ? '#166534' : '#dc2626'
                                    }}>
                                      {historyItem.status}
                                    </span>
                                  </div>
                                ))}
                                {(employeeData.history || []).length > 3 && (
                                  <button 
                                    onClick={() => openHistoryModal(employeeData)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: 'var(--symbolica-black)',
                                      cursor: 'pointer',
                                      fontSize: '13px',
                                      textDecoration: 'underline',
                                      padding: '4px 0',
                                      textAlign: 'left'
                                    }}
                                  >
                                    View {(employeeData.history || []).length - 3} more...
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {reqModalOpen && selectedReq && (
            <div className="modal-overlay" onClick={() => setReqModalOpen(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h3 style={{ marginBottom: 12 }}>Request Detail</h3>
                <p>
                  <strong>Employee:</strong> {selectedReq.userEmail || selectedReq.user_email}
                </p>
                <p><strong>Type:</strong> {labelForRequest(selectedReq, policies) === 'Sick' ? 'Sick Time' : 'PTO'}</p>
                <p>
                  <strong>Dates:</strong> {selectedReq.startDate || selectedReq.start_date} → {selectedReq.endDate || selectedReq.end_date}
                </p>
                <p>
                  <strong>Days:</strong> {selectedReq.days}
                </p>
                <p>
                  <strong>Status:</strong> {selectedReq.status}
                </p>
                <p><strong>Reason:</strong> {selectedReq.reason || 'No reason provided'}</p>
                <div style={{ textAlign: 'right', marginTop: 16 }}>
                  <button className="secondary-button" onClick={() => setReqModalOpen(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'reports' && (
        <>
          <div style={{ 
            background: '#EFECE0',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '32px',
            color: 'var(--symbolica-black)'
          }}>
            <h1 className="content-title" style={{ margin: '0 0 8px 0' }}>
              My Team – PTO Reports
            </h1>
            <p style={{ fontSize: '1rem', opacity: 0.9, margin: 0 }}>
              Comprehensive view of your direct reports' time off balances, usage, and upcoming requests
            </p>
          </div>

          {/* Search Bar */}
          <div style={{ 
            background: 'var(--symbolica-card)',
            border: '1px solid var(--symbolica-warm-border)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by employee name, email, or title..."
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: '#F6F4F0'
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    background: '#F0EDE6',
                    color: 'var(--symbolica-muted)',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                >
                  Clear
                </button>
              )}
            </div>
            {searchTerm && (
              <div style={{ marginTop: 12, fontSize: '14px', color: 'var(--symbolica-muted)' }}>
                Showing {filteredReports.length} of {reports.length} employees
              </div>
            )}
          </div>

          {reportsLoading ? (
            <div style={{ 
              background: '#F6F4F0',
              border: '1px solid var(--symbolica-warm-border)',
              borderRadius: '12px',
              padding: '24px',
              textAlign: 'center',
              marginBottom: '32px'
            }}>
              <div style={{ 
                fontSize: '48px', 
                marginBottom: '16px',
                animation: 'pulse 2s infinite'
              }}>
                📊
              </div>
              <h3 style={{ 
                fontSize: '1.25rem', 
                fontWeight: '600', 
                color: 'var(--symbolica-black)',
                margin: '0 0 8px 0'
              }}>
                Loading Team Reports
              </h3>
              <p style={{ 
                color: 'var(--symbolica-muted)', 
                fontSize: '14px',
                margin: 0
              }}>
                Gathering your team's PTO data...
              </p>
            </div>
          ) : filteredReports.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '64px',
              background: 'var(--symbolica-card)',
              borderRadius: '12px',
              border: '1px solid var(--symbolica-warm-border)'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                searchTerm ? "" : ""
              </div>
              <div style={{ color: 'var(--symbolica-muted)', fontSize: '18px', fontWeight: '500' }}>
                searchTerm ? "" : ""
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
              {filteredReports.map(rep => {
                const name = getEmployeeDisplayName({ 
                  first_name: rep.first_name, 
                  last_name: rep.last_name, 
                  email: rep.email 
                });
                const bal = rep.balance || {};
                const sickBal = rep.sick_balance || {};
                const utilizationColor = getUtilizationColor(rep.utilization_pct);
                
                // Check if employee has sick time policies
                const hasSickTime = sickBal.total > 0 || sickBal.used > 0 || sickBal.pending > 0;
                
                return (
                  <div key={rep.email} style={{ 
                    background: 'var(--symbolica-card)',
                    border: '1px solid var(--symbolica-warm-border)',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    cursor: 'default'
                  }}>
                    
                    {/* Header */}
                    <div style={{ 
                      background: '#F6F4F0',
                      padding: '20px',
                      borderBottom: '1px solid var(--symbolica-warm-border)',
                      cursor: 'default'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <div style={{ 
                          width: '40px', 
                          height: '40px', 
                          borderRadius: '50%', 
                          background: 'var(--symbolica-black)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--symbolica-black)',
                          fontSize: '16px',
                          fontWeight: '600'
                        }}>
                          {`${(rep.first_name || '').charAt(0)}${(rep.last_name || '').charAt(0)}`.toUpperCase()}
                        </div>
                        <div>
                          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: 'var(--symbolica-black)' }}>
                            {name}
                          </h3>
                          <div style={{ fontSize: '13px', color: 'var(--symbolica-muted)' }}>
                            {rep.title || '—'} • {rep.country || '—'}
                          </div>
                        </div>
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        color: 'var(--symbolica-muted)',
                        background: 'var(--symbolica-card)',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        display: 'inline-block'
                      }}>
                        Started: {formatDate(rep.start_date) || '—'}
                      </div>
                    </div>

                    {/* Metrics Grid */}
                    <div style={{ padding: '20px', cursor: 'default' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
                        <MetricCard
                          icon=""
                          label="Total Allowance"
                          value={bal.total || '0'}
                          color="#059669"
                        />
                        {bal.allow_full_year_upfront && bal.accrued_to_date != null && bal.accrued_to_date !== bal.total ? (
                          <MetricCard
                            icon="📐"
                            label="Accrued to Date"
                            value={bal.accrued_to_date}
                            color="#6366f1"
                            subtitle="pro-rata earned"
                          />
                        ) : (
                          <MetricCard
                            icon=""
                            label="Used"
                            value={bal.used || '0'}
                            color="#0891b2"
                            progress={{ value: bal.used || 0, max: bal.total || 1 }}
                          />
                        )}
                        {bal.allow_full_year_upfront && bal.accrued_to_date != null && bal.accrued_to_date !== bal.total && (
                          <MetricCard
                            icon=""
                            label="Used"
                            value={bal.used || '0'}
                            color="#0891b2"
                            progress={{ value: bal.used || 0, max: bal.total || 1 }}
                          />
                        )}
                        <MetricCard
                          icon="🔮"
                          label="Future Approved"
                          value={bal.future_approved || '0'}
                          color="#7c3aed"
                          subtitle="upcoming"
                        />
                        <MetricCard
                          icon="⏳"
                          label="Pending"
                          value={bal.pending || '0'}
                          color="#ea580c"
                          subtitle="awaiting approval"
                        />
                      </div>

                      {/* Current Balance */}
                      <div style={{
                        background: 'var(--symbolica-accent)',
                        borderRadius: '10px',
                        padding: '16px',
                        color: 'var(--symbolica-black)',
                        textAlign: 'center',
                        marginBottom: 20,
                        cursor: 'default'
                      }}>
                        <div style={{ fontSize: '13px', opacity: 0.8, marginBottom: 4 }}>
                          Remaining Balance
                        </div>
                        <div style={{ fontSize: '24px', fontWeight: '700' }}>
                          {bal.remaining || '0'} days
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.7, marginTop: 4 }}>
                          {bal.total} allowance &minus; {bal.used} used
                        </div>
                        {bal.allow_full_year_upfront && bal.accrued_to_date != null && bal.accrued_to_date !== bal.total && (
                          <div style={{ marginTop: '8px', fontSize: '13px', opacity: 0.85 }}>
                            Accrued to Date: {bal.accrued_to_date} days
                          </div>
                        )}
                        {(bal.allow_full_year_upfront || bal.has_calendar_year || bal.has_anniversary_cycle) && (
                          <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                            {bal.allow_full_year_upfront && (
                              <div 
                                onMouseEnter={() => setHoveredBadge(rep.email)}
                                onMouseLeave={() => setHoveredBadge(null)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  background: 'rgba(0, 0, 0, 0.05)',
                                  border: '1px solid rgba(0, 0, 0, 0.1)',
                                  borderRadius: '12px',
                                  padding: '4px 10px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  cursor: 'help',
                                  position: 'relative'
                                }}>
                                
                                <span>Advance booking</span>
                                {hoveredBadge === rep.email && (
                                  <div style={{
                                    position: 'absolute',
                                    bottom: '100%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    marginBottom: '8px',
                                    background: 'rgba(0, 0, 0, 0.9)',
                                    color: 'var(--symbolica-black)',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: 'normal',
                                    whiteSpace: 'normal',
                                    width: '250px',
                                    textAlign: 'center',
                                    zIndex: 1000,
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                                  }}>
                                    This employee can book against their full annual allowance upfront. "Accrued to Date" shows what they have actually earned pro-rata. Using more than accrued creates a liability that must be reconciled at year-end or termination.
                                    <div style={{
                                      position: 'absolute',
                                      top: '100%',
                                      left: '50%',
                                      transform: 'translateX(-50%)',
                                      width: 0,
                                      height: 0,
                                      borderLeft: '6px solid transparent',
                                      borderRight: '6px solid transparent',
                                      borderTop: '6px solid rgba(0, 0, 0, 0.9)'
                                    }} />
                                  </div>
                                )}
                              </div>
                            )}
                            {bal.has_calendar_year && (
                              <div 
                                onMouseEnter={() => setHoveredCalendarBadge(rep.email)}
                                onMouseLeave={() => setHoveredCalendarBadge(null)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  background: 'rgba(0, 0, 0, 0.05)',
                                  border: '1px solid rgba(0, 0, 0, 0.1)',
                                  borderRadius: '12px',
                                  padding: '4px 10px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  cursor: 'help',
                                  position: 'relative'
                                }}>
                                
                                <span>Calendar year</span>
                                {hoveredCalendarBadge === rep.email && (
                                  <div style={{
                                    position: 'absolute',
                                    bottom: '100%',
                                    right: '-10px',
                                    marginBottom: '8px',
                                    background: 'rgba(0, 0, 0, 0.9)',
                                    color: 'var(--symbolica-black)',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: 'normal',
                                    whiteSpace: 'normal',
                                    width: '280px',
                                    maxWidth: 'calc(100vw - 40px)',
                                    textAlign: 'center',
                                    zIndex: 1000,
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                                  }}>
                                    This employee's PTO follows a calendar year schedule (Jan 1 - Dec 31). Unlike anniversary-based policies, they receive their new annual allocation on January 1st, regardless of their start date. If they started mid-year, their first year was pro-rated. Each December 31st, {bal.calendar_year_carryover_info ? `${bal.calendar_year_carryover_info}` : 'unused days expire'}.
                                    <div style={{
                                      position: 'absolute',
                                      top: '100%',
                                      right: '30px',
                                      width: 0,
                                      height: 0,
                                      borderLeft: '6px solid transparent',
                                      borderRight: '6px solid transparent',
                                      borderTop: '6px solid rgba(0, 0, 0, 0.9)'
                                    }} />
                                  </div>
                                )}
                              </div>
                            )}
                            {bal.has_anniversary_cycle && (
                              <div 
                                onMouseEnter={() => setHoveredAnniversaryBadge(rep.email)}
                                onMouseLeave={() => setHoveredAnniversaryBadge(null)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  background: 'rgba(0, 0, 0, 0.05)',
                                  border: '1px solid rgba(0, 0, 0, 0.1)',
                                  borderRadius: '12px',
                                  padding: '4px 10px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  cursor: 'help',
                                  position: 'relative'
                                }}>
                                
                                <span>Anniversary cycle</span>
                                {hoveredAnniversaryBadge === rep.email && (
                                  <div style={{
                                    position: 'absolute',
                                    bottom: '100%',
                                    right: '-10px',
                                    marginBottom: '8px',
                                    background: 'rgba(0, 0, 0, 0.9)',
                                    color: 'var(--symbolica-black)',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: 'normal',
                                    whiteSpace: 'normal',
                                    width: '280px',
                                    maxWidth: 'calc(100vw - 40px)',
                                    textAlign: 'center',
                                    zIndex: 1000,
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                                  }}>
                                    This employee's PTO follows their work anniversary schedule. They receive their annual allocation on their start date anniversary each year. This is personalized to each employee - different from calendar year policies where everyone gets new PTO on January 1st. On their anniversary date, {bal.anniversary_carryover_info ? `${bal.anniversary_carryover_info}` : 'unused days expire'}.
                                    <div style={{
                                      position: 'absolute',
                                      top: '100%',
                                      right: '30px',
                                      width: 0,
                                      height: 0,
                                      borderLeft: '6px solid transparent',
                                      borderRight: '6px solid transparent',
                                      borderTop: '6px solid rgba(0, 0, 0, 0.9)'
                                    }} />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Utilization */}
                      {rep.utilization_pct !== null && (
                        <div style={{
                          background: '#F6F4F0',
                          border: '1px solid var(--symbolica-warm-border)',
                          borderRadius: '10px',
                          padding: '12px',
                          marginBottom: 20
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--symbolica-black)' }}>
                              Workdays on Non-Sick Leave
                            </span>
                            <span style={{ fontSize: '16px', fontWeight: '700', color: utilizationColor }}>
                              {rep.utilization_pct}%
                            </span>
                          </div>
                          <ProgressBar 
                            value={rep.utilization_pct} 
                            max={100} 
                            color={utilizationColor} 
                            height={8}
                          />
                        </div>
                      )}

                      {/* Sick Time Section */}
                      {hasSickTime && (
                        <div style={{
                          background: '#fef3c7',
                          border: '1px solid #f59e0b',
                          borderRadius: '10px',
                          padding: '12px',
                          marginBottom: 20
                        }}>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#92400e', marginBottom: 8 }}>
                            🤒 Sick Time
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, fontSize: '12px' }}>
                            {sickBal.is_usage_only ? (
                              <>
                                <div>Used: <strong>{sickBal.used}</strong></div>
                                <div>Pending: <strong>{sickBal.pending}</strong></div>
                              </>
                            ) : (
                              <>
                                <div>Remaining: <strong>{sickBal.remaining}</strong></div>
                                <div>Used: <strong>{sickBal.used}</strong></div>
                                <div>Pending: <strong>{sickBal.pending}</strong></div>
                              </>
                            )}
                          </div>
                          {sickBal.is_usage_only && (
                            <div style={{ fontSize: '11px', color: '#92400e', fontStyle: 'italic', marginTop: 4 }}>
                              Usage-only policy
                            </div>
                          )}
                        </div>
                      )}

                      {/* Upcoming Requests */}
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 8, 
                          marginBottom: 12,
                          fontSize: '14px',
                          fontWeight: '600',
                          color: 'var(--symbolica-black)'
                        }}>
                          
                          Upcoming Time Off
                          <span style={{
                            background: '#dbeafe',
                            color: '#1e40af',
                            padding: '2px 6px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}>
                            {(rep.upcoming || []).length}
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {(rep.upcoming || []).length === 0 ? (
                            <div style={{ 
                              textAlign: 'center', 
                              padding: '16px',
                              color: 'var(--symbolica-muted)',
                              fontSize: '13px',
                              fontStyle: 'italic',
                              background: '#F6F4F0',
                              borderRadius: '8px'
                            }}>
                              No upcoming time off scheduled
                            </div>
                          ) : (
                            (rep.upcoming || []).slice(0, 3).map(u => (
                              <div key={u.id} style={{
                                background: '#f0f9ff',
                                border: '1px solid #bae6fd',
                                borderRadius: '8px',
                                padding: '10px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}>
                                <div>
                                  <div style={{ fontSize: '13px', fontWeight: '500', color: '#0369a1' }}>
                                    {formatDate(u.start_date || u.startDate)} → {formatDate(u.end_date || u.endDate)}
                                  </div>
                                  <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)' }}>
                                    {u.days} day{u.days !== 1 ? 's' : ''}
                                  </div>
                                </div>
                                <span style={getStatusBadge(u.status)}>
                                  {u.status}
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {/* Recent History */}
                      <div>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          marginBottom: 12
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            fontSize: '14px',
                            fontWeight: '600',
                            color: 'var(--symbolica-black)'
                          }}>
                            <span>📊</span>
                            Recent History
                            <span style={{
                              background: '#f3e8ff',
                              color: '#7c2d12',
                              padding: '2px 6px',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}>
                              {(rep.history || []).length}
                            </span>
                          </div>
                          {(rep.history || []).length > 3 && (
                            <button 
                              onClick={() => openHistoryModal(rep)}
                              style={{ 
                                background: 'none', 
                                border: 'none', 
                                color: 'var(--symbolica-black)', 
                                cursor: 'pointer', 
                                fontSize: '12px', 
                                fontWeight: '500',
                                textDecoration: 'underline',
                                padding: '4px 0'
                              }}
                            >
                              View All ({(rep.history || []).length})
                            </button>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {(rep.history || []).length === 0 ? (
                            <div style={{ 
                              textAlign: 'center', 
                              padding: '16px',
                              color: 'var(--symbolica-muted)',
                              fontSize: '13px',
                              fontStyle: 'italic',
                              background: '#F6F4F0',
                              borderRadius: '8px'
                            }}>
                              No historical requests
                            </div>
                          ) : (
                            (rep.history || []).slice(0, 3).map(h => (
                              <div key={h.id} style={{
                                background: '#fefce8',
                                border: '1px solid #fde047',
                                borderRadius: '6px',
                                padding: '8px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}>
                                <div>
                                  <div style={{ fontSize: '12px', fontWeight: '500', color: '#a16207' }}>
                                    {formatDate(h.start_date || h.startDate)} → {formatDate(h.end_date || h.endDate)}
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#ca8a04' }}>
                                    {h.days} day{h.days !== 1 ? 's' : ''}
                                  </div>
                                </div>
                                <span style={getStatusBadge(h.status)}>
                                  {h.status}
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
      
      {/* Enhanced History Modal */}
      {historyModalOpen && selectedEmployeeHistory && (
        <Modal isOpen={historyModalOpen} onClose={() => setHistoryModalOpen(false)}>
          <div style={{ 
            background: '#EFECE0',
            margin: '-24px -24px 24px -24px',
            padding: '24px',
            color: 'var(--symbolica-black)'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.5rem', fontWeight: '700' }}>
              Complete PTO History
            </h3>
            <div style={{ fontSize: '1rem', opacity: 0.9 }}>
              {selectedEmployeeHistory.first_name} {selectedEmployeeHistory.last_name}
            </div>
            <div style={{ fontSize: '0.875rem', opacity: 0.8, marginTop: 4 }}>
              {selectedEmployeeHistory.title || '—'} • {selectedEmployeeHistory.country || '—'}
            </div>
          </div>
          
          {(selectedEmployeeHistory.history || []).length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '48px',
              color: 'var(--symbolica-muted)',
              background: '#F6F4F0',
              borderRadius: '12px'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
              <div style={{ fontSize: '16px', fontWeight: '500' }}>
                No historical PTO requests found
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '400px', overflowY: 'auto' }}>
              {(selectedEmployeeHistory.history || []).map(h => (
                <div key={h.id} style={{ 
                  border: '1px solid var(--symbolica-warm-border)', 
                  borderRadius: '12px', 
                  padding: '16px',
                  background: 'var(--symbolica-card)',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--symbolica-black)' }}>
                        {formatDate(h.start_date || h.startDate)} → {formatDate(h.end_date || h.endDate)}
                      </div>
                      <div style={{ fontSize: '14px', color: 'var(--symbolica-muted)', marginTop: 2 }}>
                        {h.days} day{h.days === 1 ? '' : 's'}
                      </div>
                    </div>
                    <span style={getStatusBadge(h.status)}>
                      {h.status}
                    </span>
                  </div>
                  {h.reason && (
                    <div style={{ 
                      background: '#F6F4F0',
                      border: '1px solid var(--symbolica-warm-border)',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      fontSize: '13px',
                      color: '#64748b',
                      marginTop: 8
                    }}>
                      {h.reason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
            <button 
              className="primary-button" 
              onClick={() => setHistoryModalOpen(false)}
              style={{
                background: '#EFECE0',
                border: 'none',
                color: 'var(--symbolica-black)',
                padding: '10px 20px',
                borderRadius: '8px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}